<script setup lang="ts">
  import { IconContact } from '@gat-ship/icons'
  import type { Agency } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupTextarea,
    isValidPhoneNumber,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    InputGroupPhone,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { agencyKey } from '@gat-ship/types/injection-keys'
  import { inject } from 'vue'
  import { CountryPicker } from '@gat-ship/ref-data'

  const isOpen = defineModel<boolean>('open', { default: false })
  const agencyProvider = inject(agencyKey)
  const description = ref('')

  const phoneValidators = {
    onBlur: ({ value }: { value: string }) =>
      !value || value.length === 0
        ? undefined
        : isValidPhoneNumber(value)
          ? undefined
          : 'Invalid phone number'
  }

  const changeContactInfoSchema = z.object({
    agencyId: z.string().min(1),
    address: z.string().optional(),
    countryCode: z.string().min(1, 'Please select a country'),
    email: z.string().optional(),
    mobilePhoneNumber: z.string().optional(),
    phoneNumber: z.string().optional()
  })
  type ChangeContactInfoFormValues = z.infer<typeof changeContactInfoSchema>

  const form = useForm({
    defaultValues: {
      agencyId: '',
      address: '',
      countryCode: '',
      email: '',
      mobilePhoneNumber: '',
      phoneNumber: ''
    } as ChangeContactInfoFormValues,
    validators: {
      onChange: changeContactInfoSchema
    },
    onSubmit: async ({ value }) => {
      await agencyProvider?.commands.changeContactInfo(value.agencyId, {
        address: value.address || undefined,
        countryCode: value.countryCode,
        email: value.email || undefined,
        mobilePhoneNumber: value.mobilePhoneNumber || undefined,
        phoneNumber: value.phoneNumber || undefined
      })
      isOpen.value = false
    }
  })

  export interface ShowChangeContactInfoModalParams {
    agency: Agency
  }

  const orEmpty = (value: string | null | undefined) => value ?? ''

  function showModal({ agency }: ShowChangeContactInfoModalParams) {
    description.value = `${agency.name}.`
    const contactInfo = agency.contactInfo
    form.reset({
      agencyId: agency.id,
      address: orEmpty(contactInfo?.address),
      countryCode: orEmpty(contactInfo?.country.code),
      email: orEmpty(contactInfo?.email),
      mobilePhoneNumber: orEmpty(contactInfo?.mobilePhoneNumber),
      phoneNumber: orEmpty(contactInfo?.phoneNumber)
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen" class="min-w-[400px]">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconContact class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Change Contact Info
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="description">
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="address" label="Address">
            <template #default="{ field }">
              <InputGroupTextarea
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="
                  testIds.agency.modal.changeContactInfo.addressInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="countryCode" label="Country" required>
            <template #default="{ field, invalid }">
              <CountryPicker
                :id="field.name"
                :model-value="field.state.value"
                :aria-invalid="invalid"
                :data-testid="testIds.common.select.country"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <FieldGroup>
          <FormField :form="form" name="email" label="Email">
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.agency.modal.changeContactInfo.emailInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="mobilePhoneNumber"
            label="Mobile"
            :validators="phoneValidators"
          >
            <template #default="{ field }">
              <InputGroupPhone
                :id="field.name"
                :model-value="field.state.value ?? undefined"
                :data-testid="
                  testIds.agency.modal.changeContactInfo.mobileInput
                "
                @update:model-value="(v) => field.handleChange(v ?? '')"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="phoneNumber"
            label="Phone"
            :validators="phoneValidators"
          >
            <template #default="{ field }">
              <InputGroupPhone
                :id="field.name"
                :model-value="field.state.value ?? undefined"
                :data-testid="testIds.agency.modal.changeContactInfo.phoneInput"
                @update:model-value="(v) => field.handleChange(v ?? '')"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
